// Beroemdheden: overzicht, persoon-detail, en "Toon dichtstbijzijnde beroemdheid".

import { setTitle } from '../main.js'
import { md } from '../markdown.js'
import { icon } from '../icons.js'
import { fetchImage } from '../images.js'
import { esc } from '../util.js'

const RELATION_LABELS = {
  born:      'Geboorteplek',
  lived:     'Woonde',
  worked:    'Werkte',
  died:      'Gestorven',
  connected: 'Verbonden met',
}

function surname(name) {
  const parts = String(name ?? '').trim().split(/\s+/)
  return parts[parts.length - 1] || name
}

// Bepaal "een" coord voor een persoon: eigen coord, anders de coord van z'n
// eerste gekoppelde node (bij voorkeur 'born').
function personCoord(person, nodesById) {
  if (person.coord) return { ...person.coord, source: 'self' }
  const places = person.places ?? []
  const preferred = places.find(p => p.relation === 'born') || places[0]
  if (!preferred) return null
  const n = nodesById.get(preferred.nodeId)
  if (!n?.coord) return null
  return { lat: n.coord.lat, lng: n.coord.lng, source: preferred.nodeId, relation: preferred.relation }
}

// Haversine in km
function haversineKm(a, b) {
  const R = 6371
  const toRad = d => d * Math.PI / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat)
  const s = Math.sin(dLat/2)**2 + Math.sin(dLng/2)**2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}

// ---------- Overzicht ----------
export function renderPeople(root, ctx) {
  setTitle('')
  const people = [...(ctx.data.people ?? [])].sort((a, b) =>
    surname(a.name).localeCompare(surname(b.name), 'nl')
  )

  const items = people.map(p => {
    const placeCount = p.places?.length ?? 0
    return `
      <a class="entity" data-pid="${esc(p.id)}" href="#/people/${p.id}">
        <div class="icon-bubble entity-thumb" data-thumb="${esc(p.id)}">${icon('users')}</div>
        <div class="e-body">
          <div class="e-title">${esc(p.name)}</div>
          ${p.years ? `<div class="e-meta">${esc(p.years)}</div>` : ''}
          ${p.blurb ? `<div class="e-note">${esc(p.blurb)}</div>` : ''}
          ${placeCount ? `<div class="e-meta">${placeCount} plek${placeCount === 1 ? '' : 'ken'}</div>` : ''}
        </div>
      </a>`
  }).join('')

  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/home">‹ Home</a></div>
      <h1 class="title">Beroemdheden</h1>
      <p class="subtitle">Personen die met deze regio verbonden zijn.</p>
      <div class="btn-row">
        <a class="btn secondary" href="#/nearest-celeb">${icon('navigation')} Dichtstbijzijnde tonen</a>
      </div>
      <div class="entity-list">${items || '<p class="empty">Nog geen figuren.</p>'}</div>
    </div>
  `

  // Thumbnails best-effort
  people.forEach(p => {
    fetchImage('person', p, ctx).then(url => {
      if (!url) return
      const slot = root.querySelector(`.entity-thumb[data-thumb="${CSS.escape(p.id)}"]`)
      if (slot) slot.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy"/>`
    })
  })
}

// ---------- Persoon ----------
export function renderPerson(root, ctx, id) {
  const p = ctx.indexes.peopleById.get(id)
  if (!p) {
    setTitle('')
    root.innerHTML = `<div class="page"><p class="empty">Onbekend: ${esc(id)}</p></div>`
    return
  }
  setTitle(p.name)

  const byRelation = new Map()
  for (const place of p.places ?? []) {
    if (!byRelation.has(place.relation)) byRelation.set(place.relation, [])
    byRelation.get(place.relation).push(place)
  }

  const placeBlocks = []
  for (const [rel, list] of byRelation) {
    const label = RELATION_LABELS[rel] ?? rel
    const rows = list.map(pl => {
      const n = ctx.indexes.nodesById.get(pl.nodeId)
      if (!n) return ''
      return `
        <a class="entity" href="#/node/${n.id}">
          <div class="icon-bubble">${icon('map')}</div>
          <div class="e-body">
            <div class="e-title">${esc(n.name)}</div>
            ${n.blurb ? `<div class="e-note">${esc(n.blurb).slice(0, 160)}</div>` : ''}
          </div>
        </a>`
    }).join('')
    placeBlocks.push(`<div class="card"><h2>${esc(label)}</h2><div class="entity-list">${rows}</div></div>`)
  }

  const compendium = p.compendiumId ? ctx.indexes.compendiumById.get(p.compendiumId) : null
  const compBlock = compendium ? `
    <div class="card">
      <h2>Lees meer in de encyclopedie</h2>
      <a class="entity" href="#/encyclopedie/${compendium.id}">
        <div class="icon-bubble">${icon('book-open')}</div>
        <div class="e-body">
          <div class="e-title">${esc(compendium.title)}</div>
          ${compendium.summary ? `<div class="e-note">${esc(compendium.summary.slice(0, 160))}</div>` : ''}
        </div>
      </a>
    </div>` : ''

  const links = p.links?.length ? `
    <div class="card">
      <h2>Links</h2>
      <ul class="link-list">
        ${p.links.map(l => `<li><a href="${esc(l.url)}" target="_blank" rel="noopener">${icon('external-link')} ${esc(l.label)}</a></li>`).join('')}
      </ul>
    </div>` : ''

  const eras = (p.eras ?? []).map(eid => {
    const era = ctx.indexes.eraById.get(eid)
    return era ? `<span class="badge era" style="background:${era.color}">${esc(era.label)}</span>` : ''
  }).join('')

  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/people">‹ Beroemdheden</a></div>
      <div class="entity-with-thumb">
        <div class="entity-thumb large" data-thumb="${esc(p.id)}">${icon('users')}</div>
        <div>
          <h1 class="title">${esc(p.name)}</h1>
          ${p.years ? `<p class="subtitle">${esc(p.years)}</p>` : ''}
          ${eras ? `<div class="badge-row">${eras}</div>` : ''}
        </div>
      </div>
      ${p.blurb ? `<div class="blurb">${md(p.blurb)}</div>` : ''}
      ${placeBlocks.join('')}
      ${compBlock}
      ${links}
    </div>
  `

  fetchImage('person', p, ctx).then(url => {
    if (!url) return
    const slot = root.querySelector(`.entity-thumb[data-thumb="${CSS.escape(p.id)}"]`)
    if (slot) slot.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy"/>`
  })
}

// ---------- "Dichtstbijzijnde beroemdheid" ----------
// Geolocation → bereken afstand tot ieders coord → bingo-trommel-animatie → onthul.

export function renderNearestCeleb(root, ctx) {
  setTitle('Dichtstbijzijnde beroemdheid')
  root.innerHTML = `
    <div class="page">
      <div class="crumbs"><a href="#/people">‹ Beroemdheden</a></div>
      <h1 class="title">Dichtstbijzijnde beroemdheid</h1>
      <p class="subtitle">We bepalen je positie en spelen even een trommel — de winnaar is wie het dichtst bij jou een plek heeft.</p>
      <div class="btn-row">
        <button class="btn" id="locate-btn">${icon('crosshair')} Vind 'm</button>
      </div>
      <div id="locate-status" class="empty" style="display:none"></div>
      <div id="locate-result" style="margin-top:14px"></div>
    </div>
  `
  const status = root.querySelector('#locate-status')
  const result = root.querySelector('#locate-result')
  const btn = root.querySelector('#locate-btn')

  btn.addEventListener('click', () => {
    if (!('geolocation' in navigator)) {
      status.style.display = ''
      status.textContent = 'Geen geolocatie ondersteund door deze browser.'
      return
    }
    status.style.display = ''
    status.textContent = 'Locatie wordt opgevraagd…'
    btn.disabled = true

    navigator.geolocation.getCurrentPosition(
      pos => {
        const me = { lat: pos.coords.latitude, lng: pos.coords.longitude }
        const ranked = rankCelebrities(ctx, me)
        if (!ranked.length) {
          status.textContent = 'Geen beroemdheden met bekende locatie.'
          btn.disabled = false
          return
        }
        status.textContent = ''
        playTumbler(result, ranked, ctx).then(() => { btn.disabled = false })
      },
      err => {
        status.textContent = `Locatie afgewezen of mislukt (${err.message}). Tip: sta locatie toe in je browser-instellingen.`
        btn.disabled = false
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    )
  })
}

function rankCelebrities(ctx, me) {
  const out = []
  for (const p of ctx.data.people ?? []) {
    const c = personCoord(p, ctx.indexes.nodesById)
    if (!c) continue
    out.push({ p, c, km: haversineKm(me, c) })
  }
  out.sort((a, b) => a.km - b.km)
  return out
}

async function playTumbler(slot, ranked, ctx) {
  // Korte animatie: 20 fast picks, daarna langzamer, dan onthul de winnaar.
  const winner = ranked[0]
  const all = ranked.map(r => r.p.name)
  let idx = Math.floor(Math.random() * all.length)

  slot.innerHTML = `
    <div class="celeb-tumbler">
      <div class="tumbler-frame">
        <div class="tumbler-name" id="tumbler-name">…</div>
      </div>
    </div>
  `
  const nameEl = slot.querySelector('#tumbler-name')

  const sleep = (ms) => new Promise(r => setTimeout(r, ms))
  // 12 quick spins
  for (let i = 0; i < 12; i++) {
    idx = (idx + 1 + Math.floor(Math.random() * 3)) % all.length
    nameEl.textContent = all[idx]
    nameEl.classList.add('flash')
    await sleep(70)
    nameEl.classList.remove('flash')
    await sleep(20)
  }
  // 6 slowing spins
  for (let i = 0; i < 6; i++) {
    idx = (idx + 1 + Math.floor(Math.random() * 3)) % all.length
    nameEl.textContent = all[idx]
    nameEl.classList.add('flash')
    await sleep(120 + i * 50)
    nameEl.classList.remove('flash')
  }
  // Reveal
  nameEl.textContent = winner.p.name
  nameEl.classList.add('winner')

  await sleep(400)

  slot.innerHTML = `
    <a class="entity entity-winner" href="#/people/${winner.p.id}">
      <div class="icon-bubble entity-thumb large" data-thumb="${esc(winner.p.id)}">${icon('users')}</div>
      <div class="e-body">
        <div class="e-title">${esc(winner.p.name)}</div>
        ${winner.p.years ? `<div class="e-meta">${esc(winner.p.years)}</div>` : ''}
        <div class="e-meta">≈ ${winner.km < 10 ? winner.km.toFixed(1) : Math.round(winner.km)} km hemelsbreed</div>
        ${winner.p.blurb ? `<div class="e-note">${esc(winner.p.blurb)}</div>` : ''}
      </div>
    </a>
    <div class="btn-row" style="margin-top:14px">
      <button class="btn secondary" id="redo">${icon('rotate')} Nog eens</button>
    </div>
  `
  slot.querySelector('#redo')?.addEventListener('click', () => {
    const others = ranked.filter(r => r.p.id !== winner.p.id)
    if (!others.length) return
    const shuffled = others.sort(() => Math.random() - 0.5)
    slot.innerHTML = ''
    playTumbler(slot, shuffled, ctx)
  })

  fetchImage('person', winner.p, ctx).then(url => {
    if (!url) return
    const t = slot.querySelector(`.entity-thumb[data-thumb="${CSS.escape(winner.p.id)}"]`)
    if (t) t.innerHTML = `<img src="${esc(url)}" alt="" loading="lazy"/>`
  })
}
